import { MultilineInput } from '@amelio/ui-library';
import { Controller, useFormContext } from 'react-hook-form';

import {
    AspirationFollowUp,
    AspirationQuestionBlock,
    FollowUpLabel,
} from './AspirationQuestionBlock';
import { ALIGNMENT_NOTES_MAX_LENGTH, isNewRolePath } from './careerAspirationValidation';
import { useCareerAspirationFormText } from './useCareerAspirationFormText';

import type { CareerAspirationFormValues } from './careerAspirationValidation';
import type { PathAwareQuestionProps } from './types';

import { ChoiceChips, toggleInList } from '@/components/ChoiceChips/ChoiceChips';
import { MOTIVATION_FACTORS } from '@/types/career-growth';

/** When the employee sees it happening, then, for a new role, what would make it worth it. */
export const AlignmentQuestion = ({
    employeeFirstName,
    developmentPath,
    dataTestId,
}: PathAwareQuestionProps) => {
    const { t, optionsOf, errorMessage } = useCareerAspirationFormText();
    const {
        control,
        formState: { errors },
    } = useFormContext<CareerAspirationFormValues>();

    const question = t('performanceProfile.growth.aspiration.alignment', {
        name: employeeFirstName,
    });
    const factorsQuestion = t('performanceProfile.growth.aspiration.factors');
    const showNewRoleFollowUps = isNewRolePath(developmentPath);

    return (
        <AspirationQuestionBlock
            title={question}
            dataTestId={`${dataTestId}-alignment`}
        >
            <Controller
                name={'timeAlignmentNotes'}
                control={control}
                render={({ field }) => (
                    <MultilineInput
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder={t(
                            'performanceProfile.growth.aspiration.form.alignmentPlaceholder',
                        )}
                        rows={4}
                        maxLength={ALIGNMENT_NOTES_MAX_LENGTH}
                        variant={'white'}
                        error={!!errors.timeAlignmentNotes}
                        errorMessage={errorMessage(errors.timeAlignmentNotes?.message)}
                        ariaLabel={question}
                        dataTestId={`${dataTestId}-alignment-notes`}
                    />
                )}
            />

            {showNewRoleFollowUps && (
                <AspirationFollowUp label={<FollowUpLabel>{factorsQuestion}</FollowUpLabel>}>
                    <Controller
                        name={'motivationFactors'}
                        control={control}
                        render={({ field }) => (
                            <ChoiceChips
                                options={optionsOf(
                                    MOTIVATION_FACTORS,
                                    (factor) =>
                                        `performanceProfile.growth.aspiration.factorOptions.${factor}`,
                                )}
                                selected={field.value}
                                onToggle={(value) =>
                                    field.onChange(toggleInList(field.value, value))
                                }
                                ariaLabel={factorsQuestion}
                                dataTestId={`${dataTestId}-factors`}
                            />
                        )}
                    />
                </AspirationFollowUp>
            )}
        </AspirationQuestionBlock>
    );
};
